import { type ReactNode, useLayoutEffect, useState } from 'react';

import { TableCell, TableRow } from '@/components/Table';
import { cn } from '@/lib/utils';

type DataTableExpandedRowProps = {
  children: ReactNode;
  colSpan: number;
  isExpanded: boolean;
  className?: string;
  durationMs?: number;
};

const DataTableExpandedRow = ({
  children,
  colSpan,
  isExpanded,
  className,
  durationMs = 200,
}: DataTableExpandedRowProps) => {
  const [isRendered, setIsRendered] = useState(isExpanded);
  const [isEntered, setIsEntered] = useState(isExpanded);

  /* eslint-disable react-hooks/set-state-in-effect -- keep the row mounted until the grid-row transition finishes */
  useLayoutEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (isExpanded) {
      setIsRendered(true);
      timeoutId = setTimeout(() => {
        setIsEntered(true);
      }, 0);
    } else {
      setIsEntered(false);
      timeoutId = setTimeout(() => {
        setIsRendered(false);
      }, durationMs);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [durationMs, isExpanded]);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!isRendered) {
    return null;
  }

  return (
    <TableRow className="border-0 hover:bg-transparent">
      <TableCell colSpan={colSpan} className="h-auto p-0">
        <div
          style={{ transitionDuration: `${durationMs}ms` }}
          className={cn(
            'grid w-full bg-white transition-[grid-template-rows] ease-in',
            isEntered ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div className={cn('p-3', className)}>{children}</div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default DataTableExpandedRow;
